import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Share2, TrendingUp, BarChart3, RefreshCw, ArrowLeft, 
  Calendar, Package, CreditCard, FileText, Award, Eye
} from 'lucide-react';
import { 
  FaWhatsapp, FaFacebook, FaTwitter, FaTelegram, FaInstagram, FaCopy 
} from 'react-icons/fa';
import api from '../../utils/api';
import { toast } from 'sonner';

const ShareAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/share-analytics/dashboard?days=${days}`);
      setData(response.data);
    } catch (error) {
      toast.error('Analytics लोड करने में विफल');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Platform icons and colors
  const platformConfig = {
    whatsapp: { icon: FaWhatsapp, color: 'text-green-500', bg: 'bg-green-500/10', label: 'WhatsApp' },
    facebook: { icon: FaFacebook, color: 'text-blue-600', bg: 'bg-blue-600/10', label: 'Facebook' },
    twitter: { icon: FaTwitter, color: 'text-sky-400', bg: 'bg-sky-400/10', label: 'Twitter' },
    telegram: { icon: FaTelegram, color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Telegram' },
    instagram: { icon: FaInstagram, color: 'text-pink-500', bg: 'bg-pink-500/10', label: 'Instagram' },
    copy: { icon: FaCopy, color: 'text-gray-400', bg: 'bg-gray-400/10', label: 'Link Copy' }
  };

  // Item type icons
  const itemTypeConfig = {
    product: { icon: Package, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'Products' },
    dvc: { icon: CreditCard, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Digital Cards' },
    biodata: { icon: FileText, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Bio Data' },
    auction: { icon: Award, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Auctions' },
    listing: { icon: Eye, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'Listings' }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate max for chart bars
  const maxDailyShares = data?.daily_trend?.length > 0 
    ? Math.max(...data.daily_trend.map(d => d.shares), 1) 
    : 1;

  return (
    <div className="min-h-screen bg-background" data-testid="share-analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500/20 via-background to-blue-500/10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Admin पर वापस जाएं
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center">
                <Share2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Share Analytics</h1>
                <p className="text-muted-foreground">Social sharing का पूरा डेटा देखें</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="px-3 py-2 bg-white/10 rounded-xl border-none focus:ring-2 focus:ring-primary"
                data-testid="days-filter"
              >
                <option value={7}>पिछले 7 दिन</option>
                <option value={30}>पिछले 30 दिन</option>
                <option value={90}>पिछले 90 दिन</option>
              </select>
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-white/10 rounded-xl flex items-center gap-2 hover:bg-white/20 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Total Shares Summary */}
        <div className="glass-card p-8 mb-8 text-center" data-testid="total-shares-card">
          <Share2 className="w-12 h-12 text-primary mx-auto mb-3" />
          <p className="text-5xl font-bold text-primary">{data?.total_shares || 0}</p>
          <p className="text-muted-foreground mt-2">कुल Shares ({days} दिनों में)</p>
        </div>

        {/* Platform Breakdown */}
        <div className="glass-card p-6 mb-8" data-testid="platform-breakdown">
          <h3 className="font-semibold mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Platform-wise Shares
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(platformConfig).map(([platform, config]) => {
              const count = data?.by_platform?.[platform] || 0;
              const Icon = config.icon;
              return (
                <div 
                  key={platform} 
                  className={`p-4 ${config.bg} rounded-xl text-center transition-transform hover:scale-105`}
                  data-testid={`platform-${platform}`}
                >
                  <Icon className={`w-8 h-8 ${config.color} mx-auto mb-2`} />
                  <p className={`text-2xl font-bold ${config.color}`}>{count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Item Type Breakdown */}
          <div className="glass-card p-6" data-testid="item-type-breakdown">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Item Type-wise Shares
            </h3>
            <div className="space-y-3">
              {Object.entries(itemTypeConfig).map(([type, config]) => {
                const count = data?.by_item_type?.[type] || 0;
                const percentage = data?.total_shares > 0 ? ((count / data.total_shares) * 100).toFixed(1) : 0;
                const Icon = config.icon;
                return (
                  <div key={type} className="flex items-center gap-4">
                    <div className={`w-10 h-10 ${config.bg} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{config.label}</span>
                        <span className={`text-sm font-bold ${config.color}`}>{count}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${config.bg.replace('/10', '')}`} 
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-12 text-right">{percentage}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Shared Items */}
          <div className="glass-card p-6" data-testid="top-shared-items">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              Top 10 Shared Items
            </h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data?.top_shared_items?.length > 0 ? (
                data.top_shared_items.map((item, index) => {
                  const config = itemTypeConfig[item.item_type] || itemTypeConfig.product;
                  const Icon = config.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]" title={item.item_id}>
                            {item.item_type.toUpperCase()} #{item.item_id.slice(-8)}
                          </p>
                          <p className="text-xs text-muted-foreground">{config.label}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-primary">{item.share_count} shares</span>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Share2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>अभी कोई share data नहीं है</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Daily Trend Chart */}
        <div className="glass-card p-6" data-testid="daily-trend-chart">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Daily Share Trend
          </h3>
          {data?.daily_trend?.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="flex items-end gap-2 h-40 min-w-fit pb-6">
                {data.daily_trend.map((day, i) => {
                  const height = (day.shares / maxDailyShares) * 100;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 min-w-[40px]">
                      <span className="text-xs font-medium text-primary">{day.shares}</span>
                      <div 
                        className="w-8 bg-gradient-to-t from-green-500 to-blue-500 rounded-t-lg transition-all hover:opacity-80"
                        style={{ height: `${Math.max(height, day.shares > 0 ? 8 : 0)}px` }}
                      />
                      <span className="text-xs text-muted-foreground transform -rotate-45 origin-top-left whitespace-nowrap">
                        {day.date?.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>इस समय अवधि में कोई shares नहीं</p>
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-8 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
          <p className="text-sm text-blue-300">
            <strong>ℹ️ Note:</strong> यह data तब record होता है जब users share button पर click करते हैं। 
            Products, Digital Cards, Bio Data, Auctions और Listings के shares track होते हैं।
          </p>
        </div>
      </div>
    </div>
  );
};

export default ShareAnalytics;
